// src/partials/home/DataScienceBanner.js
import React, { useState, useEffect, useRef } from 'react';

// Custom SVG Icons (These should remain as you defined them)
const StarIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" style={{ width: '1em', height: '1em' }}>
    <polygon points="12,2 15,8.5 22,9.3 17,14 18.5,21 12,17.8 5.5,21 7,14 2,9.3 9,8.5" />
  </svg>
);
const CalendarIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '1em', height: '1em' }}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /> <line x1="16" y1="2" x2="16" y2="6" /> <line x1="8" y1="2" x2="8" y2="6" /> <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const CheckCircleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" style={{ width: '1em', height: '1em' }}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);
const ArrowRightIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '1em', height: '1em' }}>
    <line x1="5" y1="12" x2="19" y2="12" /> <polyline points="12,5 19,12 12,19" />
  </svg>
);
const ZapIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" style={{ width: '1em', height: '1em' }}>
    <polygon points="13,2 3,14 12,14 11,22 21,10 12,10" />
  </svg>
);

const DataScienceBanner = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [animatedRating, setAnimatedRating] = useState(0);
  const [particles, setParticles] = useState([]);
  const [floatingElements, setFloatingElements] = useState([]);
  const bannerRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);

    const newParticles = Array.from({ length: 30 }, (_, i) => ({ // Reduced particle count for potentially better performance
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2, // Slightly smaller particles
      speedX: (Math.random() - 0.5) * 0.3,
      speedY: (Math.random() - 0.5) * 0.3,
      opacity: Math.random() * 0.6 + 0.2,
      color: ['#60A5FA', '#A855F7', '#06B6D4', '#10B981', '#F59E0B'][Math.floor(Math.random() * 5)],
      animationDelay: `${Math.random() * 4}s`
    }));
    setParticles(newParticles);

    const techElements = Array.from({ length: 10 }, (_, i) => ({ // Reduced tech elements
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      rotation: Math.random() * 360,
      scale: Math.random() * 0.4 + 0.4, // Slightly smaller scale
      symbol: ['📊', '🤖', '⚡', '🧠', '📈', '💻'][Math.floor(Math.random() * 6)],
      animationDelay: `${Math.random() * 6}s`
    }));
    setFloatingElements(techElements);

    const ratingInterval = setInterval(() => {
      setAnimatedRating(prev => {
        const nextVal = prev + 0.1;
        return nextVal > 4.9 ? 4.9 : nextVal;
      });
    }, 100);

    const handleMouseMove = (e) => {
      if (bannerRef.current) {
        const rect = bannerRef.current.getBoundingClientRect();
        setMousePosition({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(ratingInterval);
    };
  }, []);

  useEffect(() => {
    const animateParticles = () => {
      setParticles(prev =>
        prev.map(particle => ({
          ...particle,
          x: (particle.x + particle.speedX + 100) % 100,
          y: (particle.y + particle.speedY + 100) % 100,
        }))
      );
    };
    const interval = setInterval(animateParticles, 50);
    return () => clearInterval(interval);
  }, []);

  /*const features1 = [
    "Live Instructor-Led 7 Months Data Science Course",
    "Master Python, SQL, Machine Learning, Generative AI, and Deep Learning",
    "Work on Real World Projects with Industry Aligned Tools",
    "Dedicated Job Placements for Data Scientist Roles"
  ];*/
  const features = [
    <>Live Instructor-Led 7 Months <a href="https://logicmojo.com/best-data-science-courses" className="feature-link">Data Science Course</a></>,
    <>Master <a href="https://logicmojo.com/python-interview-questions" className="feature-link">Python</a>, <a href="https://logicmojo.com/machine-learning-interview-questions" className="feature-link">Machine Learning</a>, <a href="https://logicmojo.com/generative-ai-course/" className="feature-link">Generative AI</a>, and <a href="https://logicmojo.com/what-is-deep-learning/" className="feature-link">Deep Learning</a></>,
    <>Work on Real World <a href="https://logicmojo.com/data-science-projects" className="feature-link">Data Science Projects</a> with Industry Aligned Tools</>,
    <>Dedicated <a href="https://logicmojo.com/success-story" className="feature-link">Job Placements</a> for <a href="https://logicmojo.com/data-scientist-salary" className="feature-link">Data Scientist</a> Roles</>
  ];

  const companies = [
    { name: "amazon", color: "#FF9900" }, { name: "Google", color: "#4285F4" },
    { name: "Microsoft", color: "#00BCF2" }, { name: "Meta", color: "#1877F2" },
    { name: "Flipkart", color: "#2874F0" }, { name: "intel", color: "#0071C5" }
  ];

  return (
    <div ref={bannerRef} className="banner">
      <div className="background-container">
        <div
          className="dynamic-gradient"
          style={{
            background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%,
              rgba(96, 165, 250, 0.3) 0%, rgba(168, 85, 247, 0.2) 30%,
              rgba(6, 182, 212, 0.1) 60%, transparent 75%)`
          }}
        />
        {particles.map(p => (
          <div key={p.id} className="particle" style={{
            left: `${p.x}%`, top: `${p.y}%`, width: `${p.size}px`, height: `${p.size}px`,
            backgroundColor: p.color, opacity: p.opacity, boxShadow: `0 0 ${p.size * 3}px ${p.color}`, animationDelay: p.animationDelay
          }}/>
        ))}
        {floatingElements.map(el => (
          <div key={el.id} className="floating-element" style={{
            left: `${el.x}%`, top: `${el.y}%`, transform: `rotate(${el.rotation}deg) scale(${el.scale})`, animationDelay: el.animationDelay
          }}>
            {el.symbol}
          </div>
        ))}
        <div className="geometric-pattern-1" /> <div className="geometric-pattern-2" /> <div className="geometric-pattern-3" />
      </div>

      <div className="content1">
        <div className="left-content">
          <div className={`breadcrumb1 ${isVisible ? 'slide-in-left' : 'slide-out-left'}`}>
            <span className="breadcrumb-item">Home</span> <ArrowRightIcon className="breadcrumb-arrow" /> <span className="breadcrumb-current">Data Science Course</span>
          </div>
          <div className={`badge-container ${isVisible ? 'slide-in-left-delay-1' : 'slide-out-left'}`}>
            <div className="badge-glow" /> <div className="badge"> <StarIcon className="badge-icon" /> <span>Bestseller</span> </div>
          </div>
          <div className={`rating-container ${isVisible ? 'slide-in-left-delay-2' : 'slide-out-left'}`}>
            <span className="review-text">Review</span> <span className="rating-number">{animatedRating.toFixed(1)}</span>
            <div className="stars-container">
              {[...Array(5)].map((_, i) => ( <StarIcon key={i} className="star" style={{ animationDelay: `${i * 0.1}s`, transform: isVisible ? 'scale(1)' : 'scale(0)' }}/> ))}
            </div>
            <a target="_default" href="https://www.google.com/search?q=logicmojo&oq=logicmojo+&gs_lcrp=EgZjaHJvbWUyBggAEEUYOTIGCAEQRRg8MgcIAhAAGIAEMgYIAxBFGDwyBggEEEUYPDIGCAUQRRhBMgYIBhBFGDwyBggHEEUYPNIBCDQzOTJqMGo3qAIAsAIA&sourceid=chrome&ie=UTF-8&sei=ucxgaM_PE5mNseMPmdyn8A8#lrd=0x3bae15239d632ad3:0x8248087353e6d723,1,,,,"  rel="noopener noreferrer">
  <span className="ratings-link">(14890 Ratings)</span>
</a>
          </div>
          <div className={`title-container ${isVisible ? 'slide-in-left-delay-3' : 'slide-out-left'}`}>
            <h1 className="title"> <span className="title-line1">Data Science</span> <span className="title-line2">Course</span> </h1>
            <p className="subtitle">Interactive Classes With Real Time Projects</p>
          </div>
          <div className={`next-batch-container ${isVisible ? 'slide-in-left-delay-4' : 'slide-out-left'}`}>
            <div className="next-batch-glow" />
            <div className="next-batch">
              <div className="next-batch-icon"> <CalendarIcon className="calendar-icon" /> </div>
              <span className="next-batch-text">Next Batch Starting On 18th April</span>
            </div>
          </div>
          <div className="features-container">
            {features.map((feature, index) => (
              <div key={index} className={`feature-item ${isVisible ? 'slide-in-left' : 'slide-out-left'}`} style={{ animationDelay: `${0.8 + index * 0.15}s` }}>
                <div className="feature-icon-container"> <div className="feature-icon-glow" /> <CheckCircleIcon className="feature-icon" /> </div>
                <span className="feature-text">{feature}</span>
              </div>
            ))}
          </div>
          <div className={`cta-container ${isVisible ? 'slide-in-left-delay-6' : 'slide-out-left'}`} style={{ animationDelay: '1.2s' }}>
            <div className="cta-glow" />
            <a href="https://calendly.com/logicmojo/schedule-call-back-from-experts-for-live-classes?month=2025-06" target="_blank" rel="noopener noreferrer">
  <button className="cta-button">
    <ZapIcon className="cta-icon" />
    <span>Apply For Live Classes</span>
    <ArrowRightIcon className="cta-arrow" />
  </button>
</a>
          </div>
          <div className={`description-container ${isVisible ? 'slide-in-left-delay-7' : 'slide-out-left'}`} style={{ animationDelay: '1.4s' }}>
            <p className="description1"> Join the #1 Ranked <a href="https://logicmojo.com/data-science-introduction" className="feature-link">Data Science</a> Course in India, Led by <a href="https://logicmojo.com/datascience-expert-analysis/" className="feature-link">Industry Experts</a> from Top Tech Companies <br /> and Transform Your Career Today! </p>
          </div>
          <div className={`companies-container ${isVisible ? 'slide-in-left-delay-8' : 'slide-out-left'}`} style={{ animationDelay: '1.6s' }}>
            <div className="companies-grid">
              {companies.map((company) => (
                <div key={company.name} className="company-logo-item" style={{ color: company.color }}>
                  {company.name}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="right-content">
          <div className={`visual-container ${isVisible ? 'slide-in-right' : 'slide-out-right'}`}>
            <div className="holo-display">
              <div className="holo-ring1" /> <div className="holo-ring2" /> <div className="holo-ring3" />
              <div className="holo-core"> <div className="holo-center-icon">🧠</div> </div>
            </div>
            <div className="data-point data-point-1">📊</div> <div className="data-point data-point-2">⚡</div>
            <div className="data-point data-point-3">🤖</div> <div className="data-point data-point-4">💡</div>
            <div className="hand-silhouette">
              <svg viewBox="0 0 400 400" className="hand-svg">
                <defs>
                  <linearGradient id="handGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor: '#60A5FA', stopOpacity: 0.3}} />
                    <stop offset="50%" style={{stopColor: '#A855F7', stopOpacity: 0.2}} />
                    <stop offset="100%" style={{stopColor: '#06B6D4', stopOpacity: 0.15}} />
                  </linearGradient>
                  <filter id="handGlow"><feGaussianBlur stdDeviation="3" result="coloredBlur"/> <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
                </defs>
                <path d="M120 320 C140 300, 160 280, 180 270 C200 260, 220 255, 240 260 C260 265, 280 275, 300 290 C320 305, 335 325, 340 345 C345 365, 340 385, 330 400 C320 415, 305 425, 290 430 C275 435, 260 435, 245 430 L120 370 Z" fill="url(#handGradient)" filter="url(#handGlow)" className="hand-path"/>
                <circle cx="240" cy="300" r="10" fill="#60A5FA" opacity="0.7" className="tech-dot-1" />
                <circle cx="220" cy="320" r="7" fill="#A855F7" opacity="0.7" className="tech-dot-2" />
                <circle cx="260" cy="330" r="5" fill="#06B6D4" opacity="0.7" className="tech-dot-3" />
                <path d="M240 300 L280 280 M220 320 L260 300 M260 330 L300 310" stroke="#60A5FA" strokeWidth="2" opacity="0.5" className="data-stream" />
              </svg>
            </div>
            <div className="code-rain">
              {[...Array(10)].map((_, i) => ( // Reduced count for mobile
                <div key={i} className="code-column" style={{ left: `${i * 10}%`, animationDelay: `${i * 0.35}s` }}>
                  {['01','AI'][Math.floor(Math.random() * 2)]}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataScienceBanner;